import { model as router } from '!/router'
import { CategoryId } from '$/epg/categories'
import { epgChannelsModel } from '$/epg/channels'
import { $isFullscreen } from '@/effector/fullscreen'
import { A } from '@mobily/ts-belt'
import { api, type TvChannel } from '@setplex/tria-api'
import { combine, createEvent, restore, sample, type Store } from 'effector'
import { previous } from 'patronum'
import { loadEpgCategoriesModel } from '~/features/epg/categories/load'
import { selectEpgCategoryModel } from '~/features/epg/categories/select'
import { loadEpgChannelsModel } from '~/features/epg/channels/load'
import { loadMoreEpgChannelsModel } from '~/features/epg/channels/load-more'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { updateEpgChannelModel } from '~/features/epg/channels/update'
import { model as favoritesFeatureModel } from '~/features/favorites'
import { model as paymentModel } from '~/features/payment/generic'
import type { TvChannelPayment } from '~/features/payment/interfaces'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { siginInToWatchFeatureModel } from '~/features/signin-to-watch'
import { model as guestModel } from '~/features/subscriber/guest'
import { aye, nay } from '~/shared/helpers'
import { isChannelLockedAndPrevChannelNotPurchasedAndLocked } from './helpers'

// load initial epg info
export const init = createEvent<{ query: URLSearchParams }>()

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

// load more channels
export const more = createEvent()

// like/unlike channel
export const likeOrUnlike = updateEpgChannelModel.update

// try to select channel
export const trySelect = createEvent<TvChannel>()

// open pinlock popup output event
export const openPinlockPopup = createEvent<TvChannel>()

// update given channel by ids
export const updateChannels = createEvent<number[]>()

// update given channel set by ids
export const updateChannelSets = createEvent<number[]>()

// currently selected channel
// (it could be channel NOT in active channel list)
export const $selected = selectEpgChannelModel.$active

// probe channel by clicking on it
export const probe = createEvent<number>()
// probing channel id
export const $probingId = restore(probe, null)
// probing channel
export const $probing: Store<TvChannel | null> = combine(
  $probingId,
  ssePurchasesModel.$channel,
  epgChannelsModel.$all,
  (id, adjust, [channels]) => {
    const ch = id == null ? null : (channels.get(id) ?? null)
    return ch == null ? null : adjust(ch)
  }
)

export const $previouslyUnlockedId = previous(pinLockFeature.$unlockedId)
export const $previouslyUnlockedChannel: Store<TvChannel | null> = combine(
  $previouslyUnlockedId,
  ssePurchasesModel.$channel,
  epgChannelsModel.$all,
  (id, adjust, [channels]) => {
    const ch = id == null ? null : (channels.get(id) ?? null)
    return ch == null ? null : adjust(ch)
  }
)

// currently active category channels
export const $activeList: Store<TvChannel[]> = combine(
  selectEpgCategoryModel.$activeId,
  epgChannelsModel.$byCategory,
  (categoryId, [channels]) => {
    if (categoryId == null) return []
    return channels.get(categoryId) ?? []
  }
)

// pipe to check if channel is selected
export const $isChannelSelectedPipe = combine(
  $selected,
  (selected) => (channel?: TvChannel | number | string) => {
    if (selected == null || channel == null) return false
    return typeof channel === 'object'
      ? selected.id === channel.id
      : selected.id === Number(channel)
  }
)

// on every category change request to load initial set of channels, if needed
sample({
  clock: [
    init,
    selectEpgCategoryModel.$activeId,
    loadEpgCategoriesModel.$ready,
  ],
  source: {
    categoryId: selectEpgCategoryModel.$activeId,
    ready: loadEpgCategoriesModel.$ready,
  },
  filter: ({ categoryId, ready }) => categoryId != null && ready,

  fn: ({ categoryId }) => ({ categoryId: categoryId! }),
  target: loadEpgChannelsModel.load,
})

// on request from skin, load more channels for active category
sample({
  clock: more,
  source: selectEpgCategoryModel.$activeId,
  filter: (categoryId: number | null): categoryId is number =>
    categoryId != null,
  fn: (categoryId) => ({ categoryId }),
  target: loadMoreEpgChannelsModel.load,
})

// auto select first or url-chosen channel
sample({
  clock: [init, loadEpgChannelsModel.loadInitialChannelsFx.done, $activeList],
  source: {
    init: restore(init, null).reset(left, reset),
    channels: $activeList,
  },
  filter: ({ init, channels }) => init != null && channels.length > 0,

  fn: ({ init, channels }) => ({ channels, query: init!.query }),
  target: selectEpgChannelModel.autoSetActiveId,
})

// close pin lock popup (if any) when probing new channel
sample({
  clock: probe,
  target: pinLockFeature.closePinLockPopup,
})

// actually select probing channel if it is not locked (and not selected already)
sample({
  clock: probe,
  source: {
    probing: $probing,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ probing, isLocked }) => nay(isLocked(probing)),

  fn: ({ probing }) => probing!,
  target: trySelect,
})

// show pin lock popup if probing channel is locked
sample({
  clock: probe,
  source: {
    probing: $probing,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ probing, isLocked }) => aye(isLocked(probing)),

  fn: ({ probing }) => probing!,
  target: openPinlockPopup,
})

// if probing channel got unlocked, select it
sample({
  clock: pinLockFeature.$unlockedId,
  source: {
    probing: $probing,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ probing, isLocked }, unlockedId) =>
    nay(isLocked(probing)) && aye(unlockedId),
  fn: ({ probing }) => probing!,
  target: trySelect,
})

// close pin lock popup (if any) when selecting new channel
sample({
  clock: trySelect,
  target: pinLockFeature.closePinLockPopup,
})

// reset unlocked id if channel is not locked and is free or purchased
sample({
  clock: trySelect,
  filter: (channel) =>
    channel != null &&
    nay(channel.locked) &&
    (aye(channel.free) || aye(channel.purchaseInfo)),
  target: [pinLockFeature.resetUnlockId],
})

// if channel is free or purchased and is unlocked, actually select it
sample({
  clock: trySelect,
  source: pinLockFeature.$isLockedFn,
  filter: (isLocked, channel) =>
    channel !== null &&
    nay(isLocked(channel)) &&
    (aye(channel.free) || aye(channel.purchaseInfo)),
  fn: (_, channel) => channel.id,
  target: selectEpgChannelModel.setActiveId,
})

const selectTvChannelOnEpg = createEvent<TvChannelPayment>()

// otherwise, show payment popup to purchase channel
sample({
  clock: trySelect,
  source: $isFullscreen,
  filter: (isFullscreen, channel) =>
    channel !== null &&
    nay(channel.free) &&
    nay(channel.purchaseInfo) &&
    nay(isFullscreen),
  fn: (_, channel) => ({
    id: channel.id,
    channelNumber: Number(channel.channelNumber),
    categoryId: 0, // "default" value seted as we got custom categories, and redirect not used on /epg
    name: String(channel.name),
    logoUrl: String(channel.logoUrl),
    purchaseInfo: channel.purchaseInfo ?? null,
    free: Boolean(channel.free),
    priceSettings: channel.priceSettings ?? null,
    labels: null,
    featured: false, // featured not used on /epg
    externalId: channel.externalId,
  }),
  target: selectTvChannelOnEpg,
})

sample({
  clock: selectTvChannelOnEpg,
  source: {
    guest: guestModel.$guest,
    query: router.$query,
  },
  filter: ({ guest }) => aye(guest),
  fn: ({ query }, channel) => {
    const params = new URLSearchParams()

    const category = query.get('category')
    const date = query.get('date')

    if (category) params.set('category', category)
    if (date) params.set('date', date)

    return {
      name: channel.name,
      redirect: `/modules/epg?${params}`,
    }
  },
  target: siginInToWatchFeatureModel.openSignInToWatchPopup,
})

sample({
  clock: selectTvChannelOnEpg,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, channel) => channel,
  target: paymentModel.selectTvChannelOnEpg,
})

// after successful purchase, update purchased channels / sets
sample({
  clock: api.purchases.initializePurchaseProcessFx.doneData,
  source: $probing,
  fn: (probing, { items }) => {
    const channels = []
    const sets = []
    for (const { category, contentId } of items) {
      if (category === 'CHANNEL' && contentId.channelId != null) {
        channels.push(contentId.channelId)
      } else if (category === 'CHANNEL_SET' && contentId.channelSetId != null) {
        sets.push(contentId.channelSetId)
      }
    }
    return { channels, sets, channel: probing }
  },
  target: [
    updateChannels.prepend<{ channels: number[] }>(({ channels }) => channels),
    updateChannelSets.prepend<{ sets: number[] }>(({ sets }) => sets),
  ],
})

// update channels after successful purchase
sample({
  clock: updateChannels,
  filter: A.isNotEmpty,
  target: epgChannelsModel.loadManyFx,
})

// update channel sets after successful purchase
sample({
  clock: updateChannelSets,
  filter: A.isNotEmpty,
  target: epgChannelsModel.loadSetsFx,
})

// reset unlockedId for not locked channel after successful purchase and closing payment modal
sample({
  clock: paymentModel.tryClosePaymentPopup,
  source: $probing,
  filter: (channel) =>
    channel != null && aye(channel.purchaseInfo) && nay(channel.locked),
  target: [pinLockFeature.resetUnlockId],
})

// select channel after successful purchase and closing payment modal
sample({
  clock: paymentModel.tryClosePaymentPopup,
  source: $probing,
  filter: (channel) => channel != null && aye(channel.purchaseInfo),

  fn: (channel) => channel!.id,
  target: [selectEpgChannelModel.setActiveId],
})

// select channel after successful purchase via shopping cart (on SSE event)
sample({
  clock: ssePurchasesModel.$channel,
  source: {
    init: restore(init, null).reset(left, reset),
    channel: $probing,
  },
  filter: ({ init, channel }, adjust) =>
    init != null && channel != null && aye(adjust(channel).purchaseInfo),

  fn: ({ channel }) => channel!.id,
  target: [selectEpgChannelModel.setActiveId],
})

// unlock previous locked channel, if selected is locked and not purchased
sample({
  clock: paymentModel.tryClosePaymentPopup,
  source: {
    channel: $probing,
    prevChannel: $previouslyUnlockedChannel,
  },
  filter: ({ channel, prevChannel }) =>
    isChannelLockedAndPrevChannelNotPurchasedAndLocked({
      channel,
      prevChannel,
    }),

  fn: ({ prevChannel }) => prevChannel!.id,
  target: pinLockFeature.unlockId,
})

// on any category change, check for "dirty" state on favorite channels, and reload them if needed
sample({
  clock: selectEpgCategoryModel.$activeId,
  filter: (categoryId) => categoryId != null,
  target: updateEpgChannelModel.reloadFavoritesIfNeeded,
})

// if current active category is not favorite, reload favorite channels if needed, when removing channel from favorites
sample({
  clock: [updateEpgChannelModel.remove, updateEpgChannelModel.reload],
  source: selectEpgCategoryModel.$activeId,
  filter: (categoryId) =>
    categoryId != null && categoryId !== CategoryId.Favorite,
  target: updateEpgChannelModel.reloadFavoritesIfNeeded,
})

// on any favorites change OUTSIDE of module -> erase favorites channels
sample({
  clock: favoritesFeatureModel.$favorites,
  source: selectEpgChannelModel.$activeId,
  filter: (activeId) => activeId == null, // `activeId` is set to null when we left module
  target: updateEpgChannelModel.clear,
})

//
// left and reset logic
//

sample({
  clock: left,
  target: [selectEpgChannelModel.$activeId.reinit, updateEpgChannelModel.dirty],
})

// need to create new map, instead of reinit, because this will leave old map in place
sample({
  clock: reset,
  fn: () => [new Map()] as const,
  target: epgChannelsModel.$all,
})

// need to create new maps, instead of reinit, because this will leave old maps in place
sample({
  clock: reset,
  fn: () => [new Map(), new Map()] as const,
  target: epgChannelsModel.$byCategory,
})

// need to create new map, instead of reinit, because this will leave old map in place
sample({
  clock: reset,
  fn: () => [new Map()] as const,
  target: loadEpgChannelsModel.$pending,
})
